import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery } from "@connectrpc/connect-query";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronLeftIcon, ChevronRightIcon, RefreshCwIcon, WebhookIcon } from "lucide-react";
import { toast } from "sonner";

import { ErrorState } from "@/components/error-state";
import { EmptyState, PageHeader } from "@/components/page-header";
import { ToneBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { AdminService, type WebhookDelivery } from "@/gen/wabms/admin/v1/admin_pb";
import { adminKeys } from "@/lib/admin-queries";
import { errorMessage } from "@/lib/errors";
import {
  deliveryStatus,
  formatAbsolute,
  formatCount,
  formatRelative,
  type DeliveryStatus,
  type Tone,
} from "@/lib/format";
import { usePager } from "@/lib/paging";

const ALL = "__all__";
const PAGE_SIZE = 50;

const STATUS_TONE: Record<DeliveryStatus, Tone> = {
  delivered: "success",
  pending: "warning",
  abandoned: "danger",
};

function httpTone(status: number): Tone {
  if (status === 0) return "danger";
  if (status < 300) return "success";
  if (status < 500) return "warning";
  return "danger";
}

export function WebhooksPage() {
  const queryClient = useQueryClient();

  const [accountId, setAccountId] = useState<string>(ALL);
  const [pendingOnly, setPendingOnly] = useState(false);
  const [retried, setRetried] = useState<Set<string>>(new Set());

  // Filters are part of the request now, so changing one has to rewind the
  // cursor: a page token issued for a different filter is meaningless.
  const pager = usePager(`${accountId}|${String(pendingOnly)}`);

  const accountsQuery = useQuery(AdminService.method.listAccounts, {});
  const accounts = accountsQuery.data?.accounts ?? [];

  const deliveriesQuery = useQuery(AdminService.method.listWebhookDeliveries, {
    accountId: accountId === ALL ? "" : accountId,
    pendingOnly,
    pageSize: PAGE_SIZE,
    pageToken: pager.token,
  });

  const rows = useMemo(
    () => deliveriesQuery.data?.deliveries ?? [],
    [deliveriesQuery.data?.deliveries],
  );
  const nextPageToken = deliveriesQuery.data?.nextPageToken ?? "";

  const accountLabels = useMemo(
    () => new Map(accounts.map((a) => [a.accountId, a.label])),
    [accounts],
  );

  // Counts describe the page on screen, not the whole table; the contract has
  // no aggregate for this list (GetStats is per-account).
  const pendingCount = rows.filter((d) => deliveryStatus(d) === "pending").length;
  const abandonedCount = rows.filter((d) => deliveryStatus(d) === "abandoned").length;

  const retryDelivery = useMutation(AdminService.method.retryWebhookDelivery);

  async function retry(delivery: WebhookDelivery) {
    const p = retryDelivery
      .mutateAsync({ deliveryId: delivery.deliveryId })
      .then(async (res) => {
        await queryClient.invalidateQueries({ queryKey: adminKeys.webhookDeliveries() });
        return res;
      });
    toast.promise(p, {
      loading: `Retrying ${delivery.eventId}…`,
      success: "Queued for immediate redelivery.",
      error: (err: unknown) => errorMessage(err),
    });
    try {
      await p;
      setRetried((prev) => new Set(prev).add(delivery.deliveryId));
    } catch {
      // Reported by the toast.
    }
  }

  return (
    <>
      <PageHeader
        title="Webhook deliveries"
        description="Outbound event deliveries across every account, newest first. Retries back off automatically; a manual retry jumps the queue."
      />

      <div className="flex flex-wrap items-center gap-3">
        <Select value={accountId} onValueChange={setAccountId}>
          <SelectTrigger className="w-56">
            <SelectValue placeholder="All accounts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All accounts</SelectItem>
            {accounts.map((a) => (
              <SelectItem key={a.accountId} value={a.accountId}>
                {a.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2 rounded-lg border px-3 py-1.5">
          <Switch id="pending-only" checked={pendingOnly} onCheckedChange={setPendingOnly} />
          <Label htmlFor="pending-only" className="cursor-pointer text-sm font-normal">
            Pending only
          </Label>
        </div>

        <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
          <span>
            <span className="tabular font-medium text-tone-warning">
              {formatCount(pendingCount)}
            </span>{" "}
            pending on this page
          </span>
          <span>
            <span className="tabular font-medium text-tone-danger">
              {formatCount(abandonedCount)}
            </span>{" "}
            abandoned
          </span>
        </div>
      </div>

      {deliveriesQuery.isError ? (
        <ErrorState
          error={deliveriesQuery.error}
          title="Could not load webhook deliveries"
          onRetry={() => void deliveriesQuery.refetch()}
        />
      ) : deliveriesQuery.isPending ? (
        <Skeleton className="h-96 rounded-xl" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<WebhookIcon />}
          title={pendingOnly ? "Nothing pending" : "No deliveries"}
          description={
            pendingOnly
              ? "Every delivery has either succeeded or been abandoned."
              : "No events have been dispatched for this filter."
          }
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Event</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>URL</TableHead>
                <TableHead className="text-right">Attempt</TableHead>
                <TableHead>Next attempt</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last error</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((d) => {
                const status = deliveryStatus(d);
                const ownerLabel = accountLabels.get(d.accountId);
                const wasRetried = retried.has(d.deliveryId);
                return (
                  <TableRow key={d.deliveryId}>
                    <TableCell>
                      <code className="font-mono text-xs">{d.eventId}</code>
                    </TableCell>
                    <TableCell>
                      <code className="font-mono text-xs text-muted-foreground">{d.eventType}</code>
                    </TableCell>
                    <TableCell className="text-xs">
                      {ownerLabel ? (
                        <Link
                          to={`/accounts/${d.accountId}?tab=webhooks`}
                          className="hover:underline"
                        >
                          {ownerLabel}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">{d.accountId}</span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-56 truncate">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="font-mono text-xs">{d.url}</span>
                        </TooltipTrigger>
                        <TooltipContent>{d.url}</TooltipContent>
                      </Tooltip>
                    </TableCell>
                    <TableCell className="tabular text-right text-xs">{d.attempt}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {wasRetried
                        ? "now"
                        : d.nextAttemptAt
                          ? formatRelative(d.nextAttemptAt)
                          : "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <ToneBadge tone={STATUS_TONE[status]}>{status}</ToneBadge>
                        {d.lastStatus !== 0 ? (
                          <span
                            className={cnHttp(httpTone(d.lastStatus))}
                            title={`Last HTTP status ${d.lastStatus}`}
                          >
                            {d.lastStatus}
                          </span>
                        ) : (
                          <span className="text-[10px] text-muted-foreground">no response</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-72 truncate text-xs text-muted-foreground">
                      {d.lastError || "—"}
                    </TableCell>
                    <TableCell>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Retry ${d.eventId}`}
                            disabled={status === "delivered" || wasRetried}
                            onClick={() => void retry(d)}
                          >
                            <RefreshCwIcon />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {status === "delivered"
                            ? "Already delivered"
                            : wasRetried
                              ? "Retry queued"
                              : "Retry now"}
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {rows.length > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            Oldest row on this page created {formatAbsolute(rows[rows.length - 1]?.createdAt)}.
            Retrying an abandoned delivery resets its attempt counter and re-arms the backoff.
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon-sm"
              aria-label="Previous page"
              disabled={!pager.hasPrev}
              onClick={pager.goPrev}
            >
              <ChevronLeftIcon />
            </Button>
            <span className="tabular px-2 text-xs text-muted-foreground">
              page {pager.page + 1}
            </span>
            <Button
              variant="outline"
              size="icon-sm"
              aria-label="Next page"
              disabled={nextPageToken === ""}
              onClick={() => pager.goNext(nextPageToken)}
            >
              <ChevronRightIcon />
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
}

function cnHttp(tone: Tone): string {
  const base = "tabular rounded px-1 py-0.5 text-[10px] font-medium ";
  switch (tone) {
    case "success":
      return `${base}bg-tone-success/12 text-tone-success`;
    case "warning":
      return `${base}bg-tone-warning/15 text-tone-warning`;
    case "danger":
      return `${base}bg-tone-danger/12 text-tone-danger`;
    default:
      return `${base}bg-muted text-muted-foreground`;
  }
}
